import { configuredTransportOrigin } from '../../../../shared/config/api-origin';
import { CATALOG_LIMIT, CATALOG_SCOPE } from '../../consumer/catalog-contract';
import type { CapabilityDefinitionCatalogReadPort } from './capability-definition-catalog-port';
import {
  CatalogWireError,
  parseCapabilityDefinitionCatalogWire,
} from './capability-definition-catalog-wire';
export function createLiveCapabilityDefinitionCatalogAdapter({
  baseOrigin,
  fetchImpl,
}: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): CapabilityDefinitionCatalogReadPort {
  const configured = configuredTransportOrigin(baseOrigin);
  if (!configured && !/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(baseOrigin))
    throw new Error('A root local HTTP origin is required');
  const url = new URL(configured ?? baseOrigin);
  const parts = url.hostname.split('.');
  const loopback =
    url.hostname === 'localhost' ||
    url.hostname === '[::1]' ||
    (parts.length === 4 &&
      parts[0] === '127' &&
      parts.every((part) => /^\d+$/.test(part) && Number(part) <= 255));
  if (
    !configured &&
    (!loopback || url.username || url.password || url.search || url.hash || url.pathname !== '/')
  )
    throw new Error('A root local HTTP origin is required');
  return {
    async read(query, signal) {
      if (query.scope !== CATALOG_SCOPE || query.limit !== CATALOG_LIMIT)
        throw new Error('Fixed catalog query required');
      const response = await (fetchImpl ?? globalThis.fetch)(
        `${url.origin}/read-models/capability-definition-catalog`,
        { method: 'GET', credentials: 'omit', redirect: 'error', cache: 'no-store', signal },
      );
      if (response.status !== 200) throw new Error('Capability catalog HTTP request failed');
      if (
        response.headers.get('Content-Type')?.split(';')[0]?.trim().toLowerCase() !==
        'application/json'
      )
        throw new CatalogWireError();
      let value: unknown;
      try {
        value = await response.json();
      } catch (error) {
        if (error instanceof SyntaxError) throw new CatalogWireError();
        throw error;
      }
      return parseCapabilityDefinitionCatalogWire(value);
    },
  };
}
